import { useToast } from "@chakra-ui/react";
import { useState } from "react";
import { useUserData } from "../../../hooks/useUserData";
import { setFunnelModalConfig } from "../../funnel/FunnelModal";
import { FunnelContextArea } from "../../funnel/types";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { useRateLimiter } from "../../../hooks/useRateLimiter";
import { SubmitForm } from "../../comment/SubmitForm";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { useChatContext } from "../hooks/useChat";
import { getModerationErrorMessage, ModerationError } from "../types";

export function ChatSubmitForm({ disabled = false }: { disabled?: boolean }) {
  const { handleSendMessage } = useChatContext();
  const { userData } = useUserData();

  const toast = useToast();
  const [isLimited, setIsLimited] = useState(false);
  const isSmall = useIsSmallBreakpoint();

  const sendMessageWithErrorHandling = async (message: string) => {
    try {
      await handleSendMessage(message);
    } catch (error) {
      if (error instanceof ModerationError) {
        toast({
          title: getModerationErrorMessage(error.categories),
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to send message. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const rateLimitedSend = useRateLimiter(
    (message: string) => {
      sendMessageWithErrorHandling(message);
    },
    {
      rate: 5,
      per: 5000,

      onRateLimited: (timeout) => {
        setIsLimited(true);
        setTimeout(() => setIsLimited(false), timeout);
        const seconds = Math.max(Math.floor(timeout / 1000), 1);
        toast({
          title: `You are sending messages too quickly, please try again in ${seconds} seconds`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const handleSubmit = (message: string) => {
    if (isLimited) return;
    if (message.trim() === "") return;

    if (userData) {
      if (!userData.verified) {
        toast({
          title: "Please verify your account to send a message",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      } else {
        rateLimitedSend(message);
      }
    } else {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context: {
          modalContext: FunnelContextArea.globalChat,
        },
      });
    }
  };

  return (
    <SubmitForm
      autoFocus
      clearOnSubmit={!!userData?.verified}
      handleSubmit={handleSubmit}
      isDisabled={disabled || isLimited}
      isSmall={isSmall}
      placeholder="Send a message"
    />
  );
}
